/**
 * Since the start of react_v2, we have been using context to store session data.
 * However, there are certain scenarios where we need to access or modify session data
 * outside of React components, such as in utility functions or during app initialization.
 *
 * This module provides a simple session management system that allows getting,
 * setting, and listening to session changes outside of React components.
 *
 * - primary requirement - getPortfolioData()
 * - react router loaders - which run outside of react components
 */
const sessionStore = (() => {
  let session = null;
  const listeners = new Set();

  const notifyListeners = () => {
    listeners.forEach((fn) => fn(session));
  };

  return {
    get: () => (session ? { ...session } : null),
    set: (newSession) => {
      session = newSession;
      notifyListeners();
    },
    clear: () => {
      session = null;
      notifyListeners();
    },
    onChange: (callback) => {
      listeners.add(callback);
      return () => listeners.delete(callback);
    },
  };
})();

export const Session = {
  start: ({ payload }) => {
    sessionStore.set({ data: payload });
  },

  onChange: (callback) => {
    const handler = (session) => {
      callback({ payload: session?.data });
    };
    return sessionStore.onChange(handler);
  },

  get: () => {
    const session = sessionStore.get();
    return { payload: session?.data };
  },

  setPayload: (newData) => {
    const current = sessionStore.get()?.data;
    const merged = { ...current, ...newData };
    sessionStore.set({ data: merged });
    return { payload: merged };
  },

  destroy: () => {
    sessionStore.clear();
  },
};
